import {
  Box,
  ColorInput,
  Flex,
  SimpleGrid,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { SectionCard } from "../HomeBlockShared";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { PromoConfig } from "../homeBlockConfig";
import {
  AccordionList,
  applyAction,
  Crop,
  EditorLayout,
  fieldSetter,
  IconLabelList,
  iconLabelItem,
  ImagePicker,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

type PromoCard = PromoConfig["cards"][number];

const IMG = DESIGN_IMAGE.promo;

const CARD_X = [17, 281, 533, 795, 1041];

// [màu, nhãn, chữ góc, tiêu đề, mô tả, nhãn nút, link, [icon Lucide, điểm nổi bật][]]
const SAMPLE_CARDS: [string, string, string, string, string, string, string, [string, string][]][] = [
  ["#1E3A8A", "AI POWERED", "HACOM AI", "CHƯA CHẮC LAPTOP NÀO?", "Nhập ngân sách và nhu cầu sử dụng, AI HACOM sẽ gợi ý sản phẩm phù hợp chỉ trong vài giây.", "Khởi động AI HACOM", "/ai-tu-van", [["sparkles", "Gợi ý thông minh"], ["clock", "Nhanh & chính xác"], ["laptop", "Sản phẩm phù hợp"]]],
  ["#EA580C", "SHOPEE MALL", "HACOM Official Store", "DEAL TỐT TRÊN SHOPEE", "Mua hàng chính hãng, săn voucher độc quyền và nhận ưu đãi mới mỗi ngày từ HACOM Official Store.", "Mua trên Shopee", "https://shopee.vn/", [["shield-check", "Shopee Mall chính hãng"], ["badge-percent", "Voucher độc quyền"], ["truck", "Freeship toàn quốc"]]],
  ["#BE185D", "TIKTOK SHOP", "HACOM Official Store", "DEAL HOT TRÊN TIKTOK", "Xem livestream, săn voucher giới hạn và mua hàng chính hãng từ HACOM Official Store trên TikTok Shop.", "Mua trên TikTok", "https://www.tiktok.com/", [["video", "Livestream hằng ngày"], ["badge-percent", "Voucher độc quyền"], ["star", "Sản phẩm bán chạy"]]],
  ["#15803D", "PC BUILDER", "BUILD BETTER", "TỰ BUILD BỘ PC CỦA BẠN", "Tự chọn linh kiện, kiểm tra tương thích, dự đoán hiệu năng và chi phí trước khi đặt mua.", "Thử PC Builder", "/build-pc", [["cpu", "Kiểm tra tương thích"], ["sparkles", "Dự đoán hiệu năng"], ["settings", "AI gợi ý cấu hình"]]],
  ["#CA8A04", "FLASH DEAL", "SĂN NGAY KẺO LỠ", "FLASH SALE SIÊU HOT", "Ưu đãi giới hạn thời gian cho Laptop, PC Gaming, linh kiện và phụ kiện công nghệ.", "Săn Flash Deal", "/flash-sale", [["badge-percent", "Giá tốt mỗi ngày"], ["clock", "Flash Sale theo giờ"], ["package", "Số lượng có hạn"]]],
];

// Ảnh mẫu cắt từ thiết kế: chỉ để xem khi chưa tải ảnh, không lưu
const SAMPLE_CROPS = new Map<string, Crop>(
  CARD_X.map((x, i): [string, Crop] => [`promo-${i + 1}`, { x, y: 14, w: 232, h: 438 }])
);

const initialConfig = (block: HomeBlock): PromoConfig => ({
  cards:
    block.code === "home-promo-cards"
      ? SAMPLE_CARDS.map(([color, badge, corner, title, description, buttonLabel, link, points], i) => ({
          id: `promo-${i + 1}`,
          color,
          badge,
          corner,
          title,
          description,
          buttonLabel,
          link,
          points: points.map(([icon, label]) => iconLabelItem(icon, label)),
          image: null,
        }))
      : [],
});

const EditPromo = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));

  return (
    <EditorLayout block={block}>
      <SectionCard title="Thẻ quảng bá">
        <AccordionList
          items={config.cards}
          onChange={fieldSetter(setConfig, "cards")}
          itemLabel={(c) => c.title}
          renderHead={(c, i, update) => {
            const crop = SAMPLE_CROPS.get(c.id);
            return {
              title: c.title,
              subtitle: `${c.badge} · ${c.link || "chưa có link"}`,
              badge: `Thẻ ${i + 1}`,
              thumbPicker: (
                <ThumbPicker
                  label={c.title || "thẻ"}
                  src={c.image}
                  imageId={crop ? IMG : undefined}
                  crop={crop}
                  onPick={(url) => update({ image: url })}
                  onReset={() => update({ image: null })}
                  resetLabel="Gỡ ảnh"
                  width={36}
                  height={64}
                />
              ),
            };
          }}
          renderBody={(c: PromoCard, update) => {
            const crop = SAMPLE_CROPS.get(c.id);
            return (
              <Flex wrap="wrap" gap={16} align="flex-start">
                <Box style={{ flex: "0 1 240px", minWidth: 180 }}>
                  <ImagePicker
                    label="Ảnh minh hoạ"
                    src={c.image}
                    imageId={IMG}
                    crop={crop ? { x: crop.x, y: 150, w: crop.w, h: 160 } : undefined}
                    onPick={(url) => update({ image: url })}
                    onClear={c.image ? () => update({ image: null }) : undefined}
                    height={120}
                    maxWidth={240}
                  />
                </Box>
                <Stack gap={12} style={{ flex: "1 1 380px", minWidth: 0 }}>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
                    <ColorInput
                      label="Màu chủ đạo"
                      value={c.color}
                      onChange={(value) => update({ color: value })}
                      swatches={["#1E3A8A", "#EA580C", "#BE185D", "#15803D", "#CA8A04", "#B91C1C"]}
                    />
                    <TextInput
                      label="Nhãn"
                      value={c.badge}
                      onChange={(e) => update({ badge: e.currentTarget.value })}
                    />
                    <TextInput
                      label="Chữ góc phải"
                      value={c.corner}
                      onChange={(e) => update({ corner: e.currentTarget.value })}
                    />
                    <TextInput
                      label="Tiêu đề"
                      value={c.title}
                      onChange={(e) => update({ title: e.currentTarget.value })}
                    />
                  </SimpleGrid>
                  <Textarea
                    label="Mô tả"
                    autosize
                    minRows={2}
                    value={c.description}
                    onChange={(e) => update({ description: e.currentTarget.value })}
                  />
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
                    <TextInput
                      label="Nhãn nút"
                      value={c.buttonLabel}
                      onChange={(e) => update({ buttonLabel: e.currentTarget.value })}
                    />
                    <TextInput
                      label="Link"
                      value={c.link}
                      onChange={(e) => update({ link: e.currentTarget.value })}
                    />
                  </SimpleGrid>
                  <IconLabelList
                    label="Điểm nổi bật cuối thẻ"
                    items={c.points}
                    onChange={(action) =>
                      setConfig((cfg) => ({
                        ...cfg,
                        cards: cfg.cards.map((card) =>
                          card.id === c.id
                            ? { ...card, points: applyAction(action, card.points) }
                            : card
                        ),
                      }))
                    }
                    max={3}
                    addLabel="Thêm điểm nổi bật"
                  />
                </Stack>
              </Flex>
            );
          }}
          newItem={() => ({
            id: newId(),
            color: "#B91C1C",
            badge: "",
            corner: "",
            title: "",
            description: "",
            buttonLabel: "",
            link: "",
            points: [],
            image: null,
          })}
          addLabel="Thêm thẻ"
          max={5}
        />
      </SectionCard>
    </EditorLayout>
  );
};

export default EditPromo;
